import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";

// TODO: Replace with your actual web app URL
const WEB_APP_URL = "https://web.tupim.io";

const FEATURES = [
  { icon: "ri-chat-3-line", key: "download.feature.msg", descKey: "download.feature.msg.desc", color: "#00D2A8" },
  { icon: "ri-vidicon-line", key: "download.feature.call", descKey: "download.feature.call.desc", color: "#F7C948" },
  { icon: "ri-group-line", key: "download.feature.group", descKey: "download.feature.group.desc", color: "#A78BFA" },
  { icon: "ri-file-transfer-line", key: "download.feature.file", descKey: "download.feature.file.desc", color: "#F97316" },
  { icon: "ri-shield-keyhole-line", key: "download.feature.encrypt", descKey: "download.feature.encrypt.desc", color: "#34D399" },
  { icon: "ri-notification-3-line", key: "download.feature.notify", descKey: "download.feature.notify.desc", color: "#60A5FA" },
];

const BROWSERS = [
  { icon: "ri-chrome-line", name: "Chrome", color: "#00D2A8" },
  { icon: "ri-firefox-line", name: "Firefox", color: "#F97316" },
  { icon: "ri-safari-line", name: "Safari", color: "#60A5FA" },
  { icon: "ri-edge-line", name: "Edge", color: "#A78BFA" },
];

export default function PcDownload() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <div className="min-h-screen" style={{ backgroundColor: "var(--bg-base)" }}>
      <Navbar />
      <main className="pt-24 pb-20">
        {/* Hero */}
        <section className="relative py-20 overflow-hidden">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage:
                "linear-gradient(var(--grid-line) 1px, transparent 1px), linear-gradient(90deg, var(--grid-line) 1px, transparent 1px)",
              backgroundSize: "60px 60px",
            }}
          />
          <div
            className="absolute top-0 left-1/4 w-[600px] h-[400px] rounded-full pointer-events-none"
            style={{ backgroundColor: "var(--glow-primary)", filter: "blur(110px)" }}
          />
          <div
            className="absolute bottom-0 right-1/4 w-[400px] h-[300px] rounded-full pointer-events-none"
            style={{ backgroundColor: "var(--glow-secondary)", filter: "blur(90px)" }}
          />

          <div className="relative max-w-7xl mx-auto px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              {/* Left */}
              <div>
                <div
                  className="inline-flex items-center gap-2 rounded-full px-4 py-1.5 mb-6 border"
                  style={{
                    backgroundColor: "rgba(0, 210, 168, 0.10)",
                    borderColor: "rgba(0, 210, 168, 0.20)",
                  }}
                >
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-global-line" style={{ color: "var(--accent)" }}></i>
                  </div>
                  <span className="text-xs font-medium" style={{ color: "var(--accent)" }}>
                    {t("download.pc.badge")}
                  </span>
                </div>

                <h1
                  className="text-4xl md:text-5xl font-extrabold leading-tight mb-5"
                  style={{ color: "var(--text-primary)" }}
                >
                  {t("download.pc.title")}
                </h1>
                <p className="text-base leading-relaxed mb-8 max-w-lg" style={{ color: "var(--text-secondary)" }}>
                  {t("download.pc.desc")}
                </p>

                {/* Open web app button */}
                <div className="flex flex-col gap-4 mb-6 max-w-sm">
                  <a
                    href={WEB_APP_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center gap-3 px-6 py-4 rounded-2xl font-semibold text-base transition-all cursor-pointer whitespace-nowrap"
                    style={{
                      backgroundColor: "var(--accent)",
                      color: "#fff",
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.opacity = "0.9")}
                    onMouseLeave={(e) => (e.currentTarget.style.opacity = "1")}
                  >
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-external-link-line text-lg"></i>
                    </div>
                    {t("download.pc.btn.open")}
                  </a>
                </div>

                {/* Tip */}
                <p className="text-xs flex items-center gap-1.5 mb-6" style={{ color: "var(--text-muted)" }}>
                  <i className="ri-information-line"></i>
                  {t("download.pc.tip")}
                </p>

                {/* Browser support */}
                <div className="mb-6">
                  <p className="text-xs mb-3" style={{ color: "var(--text-muted)" }}>
                    {t("download.pc.browser.support")}
                  </p>
                  <div className="flex items-center gap-3">
                    {BROWSERS.map((b) => (
                      <div
                        key={b.name}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border"
                        style={{
                          backgroundColor: `${b.color}0D`,
                          borderColor: `${b.color}30`,
                        }}
                      >
                        <div className="w-4 h-4 flex items-center justify-center">
                          <i className={`${b.icon} text-sm`} style={{ color: b.color }}></i>
                        </div>
                        <span className="text-xs font-medium" style={{ color: "var(--text-secondary)" }}>
                          {b.name}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Mobile links */}
                <div className="flex items-center gap-4">
                  <button
                    onClick={() => navigate("/download/android")}
                    className="inline-flex items-center gap-1.5 text-sm transition-colors cursor-pointer"
                    style={{ color: "var(--text-secondary)" }}
                    onMouseEnter={(e) => (e.currentTarget.style.color = "var(--accent)")}
                    onMouseLeave={(e) => (e.currentTarget.style.color = "var(--text-secondary)")}
                  >
                    <i className="ri-android-line" style={{ color: "var(--accent)" }}></i>
                    Android →
                  </button>
                  <button
                    onClick={() => navigate("/download/ios")}
                    className="inline-flex items-center gap-1.5 text-sm transition-colors cursor-pointer"
                    style={{ color: "var(--text-secondary)" }}
                    onMouseEnter={(e) => (e.currentTarget.style.color = "var(--text-primary)")}
                    onMouseLeave={(e) => (e.currentTarget.style.color = "var(--text-secondary)")}
                  >
                    <i className="ri-apple-line"></i>
                    iOS →
                  </button>
                </div>
              </div>

              {/* Right: browser mockup */}
              <div className="flex items-center justify-center">
                <div className="relative w-full max-w-[520px]">
                  {/* Browser frame */}
                  <div
                    className="rounded-2xl overflow-hidden border-2 w-full"
                    style={{
                      backgroundColor: "var(--bg-surface)",
                      borderColor: "var(--border-hover)",
                    }}
                  >
                    {/* Browser chrome */}
                    <div
                      className="h-10 flex items-center px-4 gap-3 border-b"
                      style={{
                        backgroundColor: "var(--bg-subtle)",
                        borderColor: "var(--border-color)",
                      }}
                    >
                      <div className="flex gap-1.5 flex-shrink-0">
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: "#FF5F57" }}></div>
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: "#FFBD2E" }}></div>
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: "#28C840" }}></div>
                      </div>
                      {/* Address bar */}
                      <div
                        className="flex-1 h-6 rounded-full flex items-center px-3 gap-2"
                        style={{ backgroundColor: "var(--border-color)" }}
                      >
                        <div className="w-3 h-3 flex items-center justify-center flex-shrink-0">
                          <i className="ri-lock-line text-xs" style={{ color: "var(--accent)" }}></i>
                        </div>
                        <span className="text-xs truncate" style={{ color: "var(--text-muted)" }}>
                          web.tupim.io
                        </span>
                      </div>
                    </div>
                    {/* Screen content */}
                    <div className="w-full h-[300px] overflow-hidden">
                      <img
                        src="https://readdy.ai/api/search-image?query=web%20browser%20based%20messaging%20application%20dark%20theme%20interface%20with%20left%20sidebar%20showing%20contact%20list%20and%20right%20panel%20showing%20active%20chat%20conversation%2C%20teal%20green%20accent%20colors%2C%20modern%20minimalist%20clean%20UI%20design%2C%20professional%20instant%20messaging%20webapp%20screenshot%2C%20full%20browser%20viewport&width=520&height=300&seq=web-screen-main&orientation=landscape"
                        alt="TupIM Web App"
                        className="w-full h-full object-cover object-top"
                      />
                    </div>
                  </div>

                  {/* Glow */}
                  <div
                    className="absolute -bottom-4 left-1/2 -translate-x-1/2 w-72 h-10 pointer-events-none"
                    style={{ backgroundColor: "rgba(0, 210, 168, 0.12)", filter: "blur(16px)" }}
                  />

                  {/* Floating badge - No install */}
                  <div
                    className="absolute -right-6 top-8 rounded-2xl px-4 py-3 border"
                    style={{
                      backgroundColor: "var(--bg-surface)",
                      borderColor: "rgba(0, 210, 168, 0.25)",
                    }}
                  >
                    <div className="flex items-center gap-2.5">
                      <div
                        className="w-8 h-8 flex items-center justify-center rounded-xl"
                        style={{ backgroundColor: "rgba(0, 210, 168, 0.15)" }}
                      >
                        <i className="ri-check-line" style={{ color: "var(--accent)" }}></i>
                      </div>
                      <div>
                        <div className="text-xs font-semibold" style={{ color: "var(--text-primary)" }}>No Install</div>
                        <div className="text-xs" style={{ color: "var(--text-secondary)" }}>Open in browser</div>
                      </div>
                    </div>
                  </div>

                  {/* Floating badge - Sync */}
                  <div
                    className="absolute -left-6 bottom-16 rounded-2xl px-4 py-3 border"
                    style={{
                      backgroundColor: "var(--bg-surface)",
                      borderColor: "rgba(247, 201, 72, 0.25)",
                    }}
                  >
                    <div className="flex items-center gap-2.5">
                      <div
                        className="w-8 h-8 flex items-center justify-center rounded-xl"
                        style={{ backgroundColor: "rgba(247, 201, 72, 0.12)" }}
                      >
                        <i className="ri-refresh-line" style={{ color: "#F7C948" }}></i>
                      </div>
                      <div>
                        <div className="text-xs font-semibold" style={{ color: "var(--text-primary)" }}>Real-time Sync</div>
                        <div className="text-xs" style={{ color: "var(--text-secondary)" }}>All devices</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Features grid */}
        <section className="py-20" style={{ backgroundColor: "var(--bg-subtle)" }}>
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-14">
              <h2 className="text-2xl md:text-3xl font-extrabold mb-3" style={{ color: "var(--text-primary)" }}>
                {t("download.pc.features.title")}
              </h2>
              <p className="text-sm max-w-md mx-auto" style={{ color: "var(--text-secondary)" }}>
                {t("download.pc.features.subtitle")}
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {FEATURES.map((f) => (
                <div
                  key={f.key}
                  className="rounded-2xl p-6 transition-all group border"
                  style={{
                    backgroundColor: "var(--bg-surface)",
                    borderColor: "var(--border-color)",
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.borderColor = "var(--border-hover)")}
                  onMouseLeave={(e) => (e.currentTarget.style.borderColor = "var(--border-color)")}
                >
                  <div
                    className="w-10 h-10 flex items-center justify-center rounded-xl mb-4 transition-all group-hover:scale-105"
                    style={{ backgroundColor: `${f.color}15` }}
                  >
                    <i className={`${f.icon} text-lg`} style={{ color: f.color }}></i>
                  </div>
                  <h3 className="text-base font-semibold mb-1.5" style={{ color: "var(--text-primary)" }}>
                    {t(f.key)}
                  </h3>
                  <p className="text-sm leading-relaxed" style={{ color: "var(--text-secondary)" }}>
                    {t(f.descKey)}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Screenshots */}
        <section className="py-20" style={{ backgroundColor: "var(--bg-base)" }}>
          <div className="max-w-7xl mx-auto px-6">
            <h2 className="text-2xl font-extrabold text-center mb-12" style={{ color: "var(--text-primary)" }}>
              {t("download.pc.screenshots.title")}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {[
                { seq: "ss-web-1", label: "Chats", labelKey: "download.pc.ss.chats" },
                { seq: "ss-web-2", label: "Video Call", labelKey: "download.pc.ss.call" },
                { seq: "ss-web-3", label: "Group Chat", labelKey: "download.pc.ss.group" },
                { seq: "ss-web-4", label: "File Manager", labelKey: "download.pc.ss.file" },
              ].map((s) => (
                <div key={s.seq} className="flex flex-col gap-3">
                  <div
                    className="w-full h-[200px] rounded-2xl overflow-hidden border"
                    style={{ backgroundColor: "var(--bg-surface)", borderColor: "var(--border-color)" }}
                  >
                    <img
                      src={`https://readdy.ai/api/search-image?query=web%20browser%20application%20dark%20theme%20instant%20messaging%20$%7Bs.label%7D%20interface%20modern%20UI%20green%20accent%20minimal%20clean%20professional%20webapp%20screenshot%20wide%20browser%20layout%20teal%20color%20scheme&width=600&height=200&seq=${s.seq}&orientation=landscape`}
                      alt={s.label}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                  <p className="text-xs text-center" style={{ color: "var(--text-secondary)" }}>
                    {t(s.labelKey)}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Bottom CTA */}
        <section className="py-16" style={{ backgroundColor: "var(--bg-subtle)" }}>
          <div className="max-w-3xl mx-auto px-6 text-center">
            <div
              className="rounded-3xl p-10 border"
              style={{ backgroundColor: "var(--bg-surface)", borderColor: "var(--border-color)" }}
            >
              <div
                className="w-14 h-14 flex items-center justify-center rounded-2xl mx-auto mb-5"
                style={{ backgroundColor: "rgba(0, 210, 168, 0.10)" }}
              >
                <i className="ri-global-line text-2xl" style={{ color: "var(--accent)" }}></i>
              </div>
              <h3 className="text-2xl font-extrabold mb-3" style={{ color: "var(--text-primary)" }}>
                {t("download.pc.cta.title")}
              </h3>
              <p className="text-sm mb-8" style={{ color: "var(--text-secondary)" }}>
                {t("download.pc.cta.desc")}
              </p>
              <a
                href={WEB_APP_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 font-semibold text-sm px-8 py-3.5 rounded-xl transition-all cursor-pointer whitespace-nowrap"
                style={{
                  backgroundColor: "var(--accent)",
                  color: "#fff",
                }}
                onMouseEnter={(e) => (e.currentTarget.style.opacity = "0.9")}
                onMouseLeave={(e) => (e.currentTarget.style.opacity = "1")}
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-external-link-line text-sm"></i>
                </div>
                {t("download.pc.cta.btn")}
              </a>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}